import { makeAutoObservable, reaction, runInAction } from "mobx";
import agent from "../api/agent";
import { Pagination, PagingParams } from "../models/pagination";
import { AddPartToStockDto, InventoryIndexDto, NewLocationFormValues, NewVendorFormValues, TicketForVendorInventoryItemDto, UpdatePartToStockDto, VendorForInventoryIndexDto } from "../models/inventory";
import { DropdownItemProps } from "semantic-ui-react";

export default class InventoryIndexStore {
  loadingInitial = false;
  loadingTickets = false;
  constructor() {
    makeAutoObservable(this);

    reaction(
      () => this.inventoryIndexPredicate.keys(),
      () => {
        this.inventoryIndexRegistry.clear();
        this.setInventoryIndexPagingParams(new PagingParams(1, 25));
        this.setExpandedTicketsPagingParams(new PagingParams(1, 10));
        this.loadInventoryIndex();
      }
    );
  }

  setLoadingInitial = (state: boolean) => {
    this.loadingInitial = state;
  };
  locationsLoading: boolean = false;
  inventoryStockLocationList: DropdownItemProps[] = [];
  inventoryIndexPagingParams = new PagingParams(1, 25);
  expandedTicketsPagingParams = new PagingParams();

  vendorsLoading: boolean = false;
  inventoryStockVendorList: DropdownItemProps[] = [];

  inventoryIndexPredicate = new Map().set("all", true);
  inventoryIndexPagination: Pagination | null = null;
  expandedTicketsPagination: Pagination | null = null;

  navigatedToInventoryIndexItem: InventoryIndexDto | undefined = undefined; 
  expandedInventoryIndexItem: InventoryIndexDto | undefined = undefined;
  expandedInventoryIndexItemVendor: VendorForInventoryIndexDto | undefined = undefined;
  expandedTicketsPredicate = new Map();
  expandedInventoryIndexItemTickets = new Map<string | undefined, TicketForVendorInventoryItemDto>();

  inventoryIndexRegistry = new Map<string | undefined, InventoryIndexDto>();

  // Dropdown for Locations
  setDropdownLocationsLoading = (val: boolean) => {
    this.locationsLoading = val;
  }
  setInventoryStockLocationList = (locs: DropdownItemProps[]) => {
    this.inventoryStockLocationList = locs;
  }

  setDropdownVendorsLoading = (val: boolean) => {
    this.vendorsLoading = val;
  }
  setInventoryStockVendorList = (vendors: DropdownItemProps[]) => {
    this.inventoryStockVendorList = vendors;
  }

  // Inventory Index
  resetInventoryIndexPredicate = () => {
    this.inventoryIndexPredicate.clear();
  }
  setInventoryIndexPredicate = (predicate: string, value?: string) => {
    if (value)
      this.inventoryIndexPredicate.set(predicate, value);
    else
      this.inventoryIndexPredicate.delete(predicate);
  }
  hasInventoryIndexPredicate = () => {
    return Array.from(this.inventoryIndexPredicate.keys()).filter(v => v !== 'all').length > 0;
  }
  setInventoryIndexPagination = (pagination: Pagination | null) => {
    this.inventoryIndexPagination = pagination;
  }
  setNavigatedToInventoryIndexItem = (inventoryIdxItem: InventoryIndexDto | undefined) => {
    this.navigatedToInventoryIndexItem = inventoryIdxItem;
  }

  get inventoryIndexAxiosParams() {
    const params = new URLSearchParams();
    params.append("pageNumber", this.inventoryIndexPagingParams.pageNumber.toString());
    params.append("pageSize", this.inventoryIndexPagingParams.pageSize.toString());
    this.inventoryIndexPredicate.forEach((value, key) => params.append(key, value));

    return params;
  }
  get inventoryIndexTicketsAxiosParams() {
    const params = new URLSearchParams();
    if(this.expandedInventoryIndexItemVendor?.vendorId) 
      params.append("vendorId", this.expandedInventoryIndexItemVendor?.vendorId);

    params.append("pageNumber", this.expandedTicketsPagingParams.pageNumber.toString());
    params.append("pageSize", this.expandedTicketsPagingParams.pageSize.toString());

    return params;
  }

  setInventoryIndexItem = (inventoryIndexItem: InventoryIndexDto) => {
    this.inventoryIndexRegistry.set(inventoryIndexItem.inventoryStockId, inventoryIndexItem);
  }
  get getInventoryIndex() {
    return Array.from(this.inventoryIndexRegistry.values())
  }

  // Expanding an item on the inventory index
  setExpandedInventoryIndexItemTicketsPagination = (pagination: Pagination | null) => {
    this.expandedTicketsPagination = pagination;
  }
  setExpandedInventoryIndexItem = (values: InventoryIndexDto) => {
    this.expandedInventoryIndexItem = values;
  };
  setExpandedInventoryIndexItemVendor = (values: VendorForInventoryIndexDto) => {
    this.expandedInventoryIndexItemVendor = values;
  };
  setExpandedTickets = (id: string, values: TicketForVendorInventoryItemDto) => {
    this.expandedInventoryIndexItemTickets.set(id, values);
  };
  setExpandedTicketsPredicate = (predicate: string, value: string | undefined | Date) => {
    if(value) this.expandedTicketsPredicate.set(predicate, value);
    else this.expandedTicketsPredicate.delete(predicate);
  }
  get getExpandedInventoryIndexItemTickets() {
    return Array.from(this.expandedInventoryIndexItemTickets.values())
  }


  // Api Calls
  loadDropdownListStockLocations = async () => {
    this.locationsLoading = true;
    try {
      const result = await agent.inventoryIndex.dropDownStockLocationsList();
      runInAction(() => {
        this.setInventoryStockLocationList(result);
      });
    } catch (error) {
      console.log(error);
    } finally {
      this.setDropdownLocationsLoading(false);
    }
  };
  addNewLocation = async (values: NewLocationFormValues) => {
    this.locationsLoading = true;
    let result = null;
    try {
      result = await agent.inventoryIndex.addNewLocation(values);
    } catch(error) {
      console.log(error);
    } finally {
      this.locationsLoading = false;
      return result;
    }
  }

  loadDropdownListStockVendors = async () => {
    this.vendorsLoading = true;
    try {
      runInAction(() => {
        this.setInventoryStockVendorList([]);  // FIXME
      });
    } catch (error) {
      console.log(error);
    } finally {
      this.setDropdownVendorsLoading(false);
    }
  };

  addNewVendor = async (values: NewVendorFormValues) => {
    this.vendorsLoading = true;
    try {
      // FIXME
    } catch (error) {
      console.log(error);
    } finally {
      this.vendorsLoading = false;
    }
  }

  loadInventoryIndex = async () => {
    this.loadingInitial = true;
    try {
      this.resetInventoryIndexRegistry();
      const result = await agent.inventoryIndex.getInventoryIndex(this.inventoryIndexAxiosParams);
      runInAction(() => {
        result.data.forEach(dataItem => this.setInventoryIndexItem(dataItem))
      });
      this.setInventoryIndexPagination(result.pagination);
      return result;
    } catch (error) {
      console.log(error);
    } finally {
      runInAction(() => {
        this.loadingInitial = false;
      });
    }
  };

  loadExpandedInventoryIndexItemTickets = async (id: string) => {
    this.loadingTickets = true;
    try {
      const invItmTicketsResult = await agent.inventoryIndex.getInventoryIndexTickets(id, this.inventoryIndexTicketsAxiosParams);
      runInAction(() => {
        invItmTicketsResult.data.forEach(dataItem => this.setExpandedTickets(dataItem.ticketId, dataItem));
      });
      this.setExpandedInventoryIndexItemTicketsPagination(invItmTicketsResult.pagination);
    } catch (error) {
      console.log(error);
    } finally {
      runInAction(() => {
        this.loadingTickets = false;
      });
    }
  }

  // API Calls for mutations to data, in order of add, update and remove
  addPartToStock = async (values: AddPartToStockDto) => {
    this.loadingInitial = true;
    try {
      await agent.inventoryIndex.addPartToInventory(values);
    } catch (error) {
      console.log('error:', error);
    } finally {
      runInAction(() => {
        this.loadingInitial = false;
      });
    }
  }

  updatePartInStock = async (values: UpdatePartToStockDto) => {
    this.loadingInitial = true;
    try {
      await agent.inventoryIndex.updatePartInInventory(values);
    } catch (error) {
      console.log('error:', error);
    } finally {
      runInAction(() => {
        this.loadingInitial = false;
      });
    }
  }
  updateQuantityInStock = async (partId: string, quantityUsed?: number) => {
    this.loadingInitial = true;
    try {
      await agent.inventoryIndex.updateQuantityInStock(partId, { quantity: quantityUsed ?? 0 });
    } catch (error) {
      console.log('error:', error);
    } finally {
      runInAction(() => {
        this.loadingInitial = false;
      });
    }
  }

  removePart = async (partId: string) => {
    this.loadingInitial = true;
    try {
      await agent.inventoryIndex.removePart(partId);
      this.inventoryIndexRegistry.delete(partId);
    } catch (error) {
      console.log('error:', error);
    } finally {
      runInAction(() => {
        this.loadingInitial = false;
      });
    }
  }

  clearInventoryIndexFilters = () => {
    this.inventoryIndexPredicate.clear();
  }
  resetInventoryIndexRegistry = () => {
    this.inventoryIndexRegistry.clear();
  }
  resetExpandedTicketsRegistry = () => {
    this.expandedInventoryIndexItemTickets.clear();
  }

  setInventoryIndexPagingParams = (pagingParams: PagingParams) => {
    this.inventoryIndexPagingParams = pagingParams;
  }

  setExpandedTicketsPagingParams = (pagingParams: PagingParams) => {
    this.inventoryIndexPagingParams = pagingParams;
  }
}
